import { useRef } from 'react';
import type { ProjectTask } from '@/types/odoo';

/** The fase a task belongs to: its parent, or itself when it is a fase. */
function faseOf(task: ProjectTask): number {
  return Array.isArray(task.parent_id) ? task.parent_id[0] : task.id;
}

/** Deadline day (YYYY-MM-DD) of a task, or null without a deadline. */
function dayOf(task: ProjectTask): string | null {
  return task.date_deadline ? task.date_deadline.slice(0, 10) : null;
}

export interface InstallateurTaskOptions {
  /** The Dashboard's selected fase; null for "Alles" (and the standalone tab). */
  faseId: number | null;
  /** The project view's active task (a Planning click, a ?task= link, or the one
   *  ProjectView auto-selects on open). It wins on "Alles" and inside its own fase. */
  activeTaskId: number | null;
  /** The user chose the active task and has not picked another fase since: it wins
   *  in any fase (see useChosenTaskWins). */
  activeTaskChosen?: boolean;
  /** Today as YYYY-MM-DD. */
  today: string;
}

/**
 * The task (day) the Installateur Taak section shows; its description and its
 * installers both follow this one task.
 * - A task the user chose wins in any fase until they pick another fase. The active
 *   task also wins on "Alles" and when it belongs to the selected fase, so a fase
 *   click always shows that fase, also when ProjectView auto-selected a task of
 *   another fase (or a task without a fase) on open.
 * - "Alles": the nearest task due today or later, else the first one.
 * - A fase: only its tasks. Today inside the fase gives today's task or its next day;
 *   a fase fully in the past or the future gives its first day.
 */
export function pickInstallateurTask(
  tasks: ProjectTask[],
  { faseId, activeTaskId, activeTaskChosen = false, today }: InstallateurTaskOptions,
): ProjectTask | null {
  const inFase = (t: ProjectTask) => faseId === null || faseOf(t) === faseId;
  const active = activeTaskId ? tasks.find((t) => t.id === activeTaskId) : undefined;
  if (active && (activeTaskChosen || inFase(active))) return active;
  const pool = tasks.filter(inFase);
  const dated = pool
    .filter((t) => dayOf(t) !== null)
    .sort((a, b) => (a.date_deadline as string).localeCompare(b.date_deadline as string));
  const next = dated.find((t) => (dayOf(t) as string) >= today);
  if (faseId === null) return next ?? pool[0] ?? null;
  return next ?? dated[0] ?? pool[0] ?? null;
}

/** Today as YYYY-MM-DD, as the section has always compared deadlines (UTC). */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The Dashboard's fase: a fase id, 'alles', or null while the fases load (and on the standalone tab). */
export type ShownFase = number | 'alles' | null;

/**
 * Whether the user's chosen task (choice > 0, see the store's taskChoice) still wins:
 * yes in the fase shown when it was chosen, also when that is the Dashboard's default
 * fase loading afterwards; no from the first fase the user picks after it.
 */
export function useChosenTaskWins(choice: number, fase: ShownFase): boolean {
  const pin = useRef({ choice: 0, fase: null as ShownFase, wins: false });
  if (choice !== pin.current.choice) pin.current = { choice, fase, wins: choice > 0 };
  else if (pin.current.fase === null) pin.current.fase = fase;
  else if (fase !== pin.current.fase) pin.current.wins = false;
  return pin.current.wins;
}
